import groupBy from 'lodash/groupBy';
import { compareAsc } from 'date-fns';
import { EXCHANGE_LOGOS, SUPPORTED_EXCHANGES } from '@/lib/constants';
import { ALL_FEATURES, AVAILABLE_FEATURES } from '@/lib/constants/exchanges';
import type { ExchangeAccount } from '@/state/exchanges/exchangesSliceTypes';
import type { InitialMemberState } from '@/state/member/memberTypes';
import type { MultiFetchAccount } from '@/state/shortStorage/shortStorageTypes';
import { EXCHANGES } from '@/lib/types/generalTypes';
import { PLANS } from '@/state/payments/paymentsTypes';

export interface ExchangeListItem {
    visibleName: string;
    accountsNumber: number;
    logo: any;
    id: EXCHANGES;
    favNumber: number;
    isDemo: boolean;
}

export type AccountNamesMap = {
    [accountId: number]: {
        keyError?: null | string;
        label: string;
        exchangeName: string;
        type: EXCHANGES;
        isUnlocked: boolean | null;
        accountId: number;
    };
};

export const selectAccountNames = (accounts: ExchangeAccount[]): AccountNamesMap => {
    if (!accounts) return {};
    const map: AccountNamesMap = {};

    accounts.forEach((account) => {
        map[account.id] = {
            label: account.label,
            exchangeName: SUPPORTED_EXCHANGES[account.exchangeId]?.visibleName,
            type: account.exchangeId,
            isUnlocked: account.isUnlocked,
            accountId: account.id,
            keyError: account.keyError,
        };
    });
    return map;
};

export const selectExchangeList = (accounts: ExchangeAccount[]): ExchangeListItem[] => {
    const grouped = groupBy(accounts, 'exchangeId');
    return Object.keys(grouped).map((key) => ({
        visibleName: SUPPORTED_EXCHANGES[key]?.visibleName,
        accountsNumber: accounts.filter((item) => item.exchangeId === key).length,
        logo: EXCHANGE_LOGOS[key],
        id: key as EXCHANGES,
        favNumber: accounts.filter((item) => item.isFavorite && item.exchangeId === key).length,
        isDemo: accounts.some((item) => item.isDemo),
    }));
};

export const selectAccountById = (
    accounts: ExchangeAccount[],
    id: number | string | null | undefined | false,
): ExchangeAccount | null => {
    if (!id) return null;
    return accounts.find((account) => account.id === +id) ?? null;
};

export const selectUnlockedAccounts = (accounts: ExchangeAccount[]): ExchangeAccount[] =>
    accounts.filter((account) => account.isUnlocked);

export const selectIsDemoAvailable = (accounts: ExchangeAccount[]): boolean =>
    accounts.length === 1 && Boolean(accounts[0]?.isDemo);

export const selectHasValidExchangesLength = (accounts: ExchangeAccount[]): boolean =>
    accounts.some((item) => !item.isDemo && item.active && !item.keyError);

export const selectAccountsNav = (accounts: ExchangeAccount[]): ExchangeAccount[] =>
    accounts.filter((item) => item.active);

export const selectAccountsByIds = (
    accounts: ExchangeAccount[],
    accountIds: number[],
): ExchangeAccount[] => accounts.filter((account) => accountIds.includes(account.id));

export const selectAccountsByExchange = (
    accounts: ExchangeAccount[],
    selectedExchange: EXCHANGES | null | undefined,
): ExchangeAccount[] => {
    if (!selectedExchange || !Object.values(EXCHANGES).includes(selectedExchange)) return [];
    return accounts.filter((account) => account.exchangeId === selectedExchange);
};

export interface SelectAccountIdsParams {
    exchange: EXCHANGES | string | null | undefined;
    withUnlocked?: boolean;
}

export const selectAccountIds = (
    accounts: ExchangeAccount[],
    { exchange, withUnlocked = true }: SelectAccountIdsParams,
): number[] => {
    if (!exchange) return [];
    return accounts
        .filter((account) => {
            if (!withUnlocked) {
                return account.exchangeId === exchange && !account.isUnlocked;
            }
            return account.exchangeId === exchange;
        })
        .map((account) => account.id);
};

export const selectAccountIdsByExchange = (
    accounts: ExchangeAccount[],
): { [exchange: string]: number[] } => {
    const result: { [exchange: string]: number[] } = {};
    [...accounts]
        .sort((a, b) => compareAsc(new Date(a.created), new Date(b.created)))
        .forEach((account) => {
            if (!result[account.exchangeId]) {
                result[account.exchangeId] = [];
            }
            if (!account.isUnlocked) {
                result[account.exchangeId].push(account.id);
            }
        });

    return result;
};

export const selectAccountForMultiFetch = (
    accounts: ExchangeAccount[],
    member: InitialMemberState,
): MultiFetchAccount[] => {
    const oldestAccounts: Record<string, ExchangeAccount> = {};
    accounts.forEach((account) => {
        const currentOldest = oldestAccounts[account.exchangeId];
        if (!currentOldest || account.id < currentOldest.id) {
            oldestAccounts[account.exchangeId] = account;
        }
    });

    const { accountType: memberPlan } = member.profile.accountType;

    const hasAnalyticsAccess = AVAILABLE_FEATURES[memberPlan].includes(ALL_FEATURES.ANALYTICS);
    const hasReportsAccess = AVAILABLE_FEATURES[memberPlan].includes(ALL_FEATURES.REPORTS);

    return accounts.map((account) => {
        const currentOldest = oldestAccounts[account.exchangeId];
        const isOldestAccount = +account.id === +currentOldest.id;

        const isAnalyticsReady =
            Boolean(account.isUnlocked) ||
            (hasAnalyticsAccess && (memberPlan !== PLANS.PRO || isOldestAccount));

        const isReportsReady =
            Boolean(account.isUnlocked) ||
            (hasReportsAccess && (memberPlan !== PLANS.PRO || isOldestAccount));

        return {
            label: account.label,
            exchangeName: SUPPORTED_EXCHANGES?.[account.exchangeId]?.visibleName,
            type: account.exchangeId,
            isUnlocked: account.isUnlocked,
            accountId: +account.id,
            keyError: account.keyError,
            isAnalyticsReady,
            isReportsReady,
        };
    });
};
